<template>
  <div>
    <h1>{{ $t("booking.booking") }} {{ booking.reservation_number }}</h1>

    <v-row class="flight-header">
      <v-col class="header-flight-part-block flight-part white" cols="4">
        <div style="display: inline-flex">
          <v-icon class="header-flight-icon">flight_takeoff</v-icon>
        </div>
        <div style="display: inline-block">
          <div class="flight-direction grey--text">
            {{ $t("booking.flight.departure") }}
          </div>
          <div class="flight-airport">
            <span class="bigger" style="font-weight: bold">
              <v-tooltip bottom color="white">
                <span style="color: #0f0f0f">{{ booking.start_airport_name }} </span>
                <template #activator="{on}">
                  <span class="accent--text" v-on="on">{{ booking.start_airport_code }}</span>
                </template>
              </v-tooltip>
              <span class="arrow">➞</span>
              <v-tooltip bottom color="white">
                <template #activator="{on}">
                  <span class="accent--text" v-on="on">{{ booking.end_airport_code }} </span>
                </template>
                <span style="color: #0f0f0f">{{ booking.end_airport_name }}</span>
              </v-tooltip>
            </span>
            {{ booking.start_time != null ? booking.start_time.substr(0, 5) : $t("global.noData") }}
            -
            {{ booking.end_time != null ? booking.end_time.substr(0, 5) : $t("global.noData") }}
          </div>
          <div class="flight-date grey--text">
            {{ booking.start_date != null ? dayOfWeek(booking.start_date) : $t("global.noData") }}
            {{ booking.start_date != null ? formatDateView(booking.start_date) : $t("global.noData") }}
          </div>
        </div>
      </v-col>
      <v-col class="header-flight-part-block flight-part white " v-if="booking.return_start_airport_code != null" cols="4">
        <div style="display: inline-flex">
          <v-icon class="header-flight-icon">flight_land</v-icon>
        </div>
        <div style="display: inline-block">
          <div class="flight-direction grey--text">
            {{ $t("booking.returnRoute") }}
          </div>
          <div class="flight-airport">
            <span class="bigger" style="font-weight: bold">
              <v-tooltip bottom color="white">
                <template #activator="{on}">
                  <span class="accent--text" v-on="on">{{ booking.return_start_airport_code }}</span>
                </template>
                <span style="color: #0f0f0f">{{ booking.return_start_airport_name }}</span>
              </v-tooltip>
              <span class="arrow">➞</span>
              <v-tooltip bottom color="white">
                <template #activator="{on}">
                  <span class="accent--text" v-on="on">{{ booking.return_end_airport_code }}</span>
                </template>
                <span style="color: #0f0f0f">{{ booking.return_end_airport_name }}</span>
              </v-tooltip>
            </span>
            {{ booking.return_start_time != null ? booking.return_start_time.substr(0, 5) : $t("global.noData") }}
            -
            {{ booking.return_end_time != null ? booking.return_end_time.substr(0, 5) : $t("global.noData") }}
          </div>
          <div class="flight-date grey--text">
            {{ booking.return_start_date != null ? dayOfWeek(booking.return_start_date) : $t("global.noData") }}
            {{ booking.return_start_date != null ? formatDateView(booking.return_start_date) : $t("global.noData") }}
          </div>
        </div>
      </v-col>
    </v-row>

    <h2>{{ $t("booking.bookingDetails") }}</h2>

    <div class="grey--text pa-2">
      <v-icon class="mr-1">airplanemode_active</v-icon>
      {{ booking.airline_name }}
    </div>
    <v-divider style="margin-bottom: 15px" />

    <v-expansion-panels flat accordion>
      <v-expansion-panel class="pa-0 upper-line">
        <v-expansion-panel-header class="">
          <booking-flight-part-detail
            :end_airport_code="booking.end_airport_code"
            :end_airport_name="booking.end_airport_name"
            :end_date="booking.end_date"
            :end_time="booking.end_time"
            :label="$t('booking.flight.departure')"
            :start_airport_code="booking.start_airport_code"
            :start_airport_name="booking.start_airport_name"
            :start_date="booking.start_date"
            :start_time="booking.start_time"
            :transfers="travelTransfersFirstPart"
          />
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <booking-flight-route-collapse :routes="booking.routes.filter(route => route.is_return_route == 0)" />
        </v-expansion-panel-content>
      </v-expansion-panel>

      <template v-if="booking.return_start_date != null">
        <v-expansion-panel class="pa-0 upper-line mt-8">
          <v-expansion-panel-header>
            <booking-flight-part-detail
              :end_airport_code="booking.return_end_airport_code"
              :end_airport_name="booking.return_end_airport_name"
              :end_date="booking.return_end_date"
              :end_time="booking.return_end_time"
              :label="$t('booking.returnRoute')"
              :start_airport_code="booking.return_start_airport_code"
              :start_airport_name="booking.return_start_airport_name"
              :start_date="booking.return_start_date"
              :start_time="booking.return_start_time"
              :transfers="travelTransfersSecondPart"
            />
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <booking-flight-route-collapse :routes="booking.routes.filter(route => route.is_return_route == 1)" />
          </v-expansion-panel-content>
        </v-expansion-panel>
      </template>
    </v-expansion-panels>

    <h2 class="mt-3">{{ $t("booking.passengers") }}</h2>

    <!-- eslint-disable-next-line -->
    <div v-for="passenger in booking.passengers">
      <fieldset class="pd-1 custom-fieldset">
        <v-row>
          <v-col class="text-center align-content-center fill-height align-content-center" md="1" cols="1">
            <v-icon style="font-size: 46px; color: rgba(0, 0, 0, 0.32);">fa-user-circle</v-icon>
          </v-col>
          <v-col cols="5">
            <v-text-field :label="$t('booking.passenger')" :value="passenger" class="no-line-after-input" disabled style="text-transform: capitalize" />
          </v-col>
        </v-row>
      </fieldset>
    </div>

    <template v-if="$store.getters.hasPermission('viewInvoices')">
      <h2>{{ $t("global.invoice") }}</h2>

      <span v-if="booking.invoices.length === 0">
        {{ $t("booking.invoiceNotSet") }}
      </span>

      <span v-else v-for="invoice of booking.invoices" :key="invoice.id">
        <v-row>
          <v-col class="text-center align-content-center fill-height align-content-center" md="1" cols="1">
            <v-icon style="font-size: 46px; color: rgba(0, 0, 0, 0.32);">mdi-file-document</v-icon>
          </v-col>
          <v-col md="3" cols="5">
            <v-text-field :label="$t('invoice.details.invoiceNumber')" :value="invoice.number" class="no-line-after-input" disabled />
          </v-col>
          <v-col>
            <v-row v-for="invoiceItem of invoice.items" :key="invoiceItem.id">
              <v-text-field
                :label="$t('booking.other.serviceName')"
                class="no-line-after-input"
                disabled
                :value="invoiceItem.name + ' ' + invoiceItem.gross_in_currency + ' ' + invoiceItem.currency"
              />
            </v-row>
          </v-col>
          <v-spacer />
          <v-col class="text-right" cols="1">
            <v-btn :to="'/invoices/details/' + invoice.id" icon>
              <v-icon>remove_red_eye</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </span>
    </template>
  </div>
</template>

<script>
import dayOfWeek from "../../repository/shortDayOfWeekFromDate";
import filterOverAllProperty from "../../repository/filterOverAllProperty";
import getTextDateDiff from "../../repository/getTextDateDiff";
import FlightBooking from "../../models/booking/flightBooking";
import BookingFlightPartDetail from "../../components/Booking/FlightDetails/BookingFlightPartDetail.vue";
import BookingFlightRouteCollapse from "../../components/Booking/FlightDetails/BookingFlightRouteCollapse.vue";
import formatDateView from "../../repository/fotmatDateView";

export default {
  name: "BookingFlightDetailView",
  components: { BookingFlightRouteCollapse, BookingFlightPartDetail },
  data: () => ({
    search: "",
    booking: new FlightBooking()
  }),
  computed: {
    travelTimeFirstPart() {
      let startDateTime = this.booking.start_date + " " + this.booking.start_time;
      let endDateTime = this.booking.end_date + " " + this.booking.end_time;
      return getTextDateDiff(startDateTime, endDateTime);
    },
    travelTimeSecondPart() {
      let startDateTime = this.booking.return_start_date + " " + this.booking.return_start_time;
      let endDateTime = this.booking.return_end_date + " " + this.booking.return_end_time;
      return getTextDateDiff(startDateTime, endDateTime);
    },
    travelTransfersFirstPart() {
      return this.booking.routes.filter(route => route.is_return_route === 0).length - 1;
    },
    travelTransfersSecondPart() {
      return this.booking.routes.filter(route => route.is_return_route === 1).length - 1;
    }
  },
  methods: {
    viewBooking: function(booking) {
      this.$store.dispatch("Booking/loadBookingDetails", booking);
    },
    dayOfWeek: dayOfWeek,
    filterOverAllProperty: filterOverAllProperty,
    formatDateView: formatDateView
  },
  created() {
    if (typeof this.$route.params.id !== "undefined") {
      this.booking.load(this.$route.params.id);
    }
  },
  mounted() {
    this.$store.dispatch("Visual/setHalfBackground");
  },
  beforeDestroy() {
    this.$store.dispatch("Visual/setWhiteBackground");
  }
};
</script>

<style scoped>
.chip--small {
  height: 22px;
}

.flight-direction {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}

.grey--text {
  color: #9e9e9e !important;
}

.bigger {
  margin-right: 4px;
  font-size: 15px;
}

.flight-airport {
  font-weight: 700;
  line-height: 1;
  font-size: 16px;
}

.header-flight-part-block {
  background-color: white;
  margin-left: 15px;
  margin-right: 15px;
  padding: 15px;
  box-shadow: 0px 8px 10px -5px rgba(0, 0, 0, 0.2), 0px 16px 24px 2px rgba(0, 0, 0, 0.14), 0px 6px 30px 5px rgba(0, 0, 0, 0.12);
}

.header-flight-icon {
  padding: 0 30px;
  font-size: 50px;
}

.flight-airport > .bigger {
  font-size: 24px;
}

.flight-header {
  margin-top: 30px;
  margin-bottom: 75px;
}

.flight-divider {
  font-weight: 700;
  position: relative;
  top: -20px;
}

.flight-divider-text {
  text-transform: uppercase;
}

.flight-content {
  font-size: 24px;
  font-weight: 700;
}

.flight-content-airport-code {
  font-size: 18px;
}

.flight-content-airport-name {
  color: #909194;
}

.flight-content-time {
}

.dot {
  position: relative;
  /*top: -3px;*/
  /*left: 50%;*/
  width: 6px;
  height: 6px;
  background-color: #44d3af;
  border-radius: 50%;
  display: inline-block;
  margin: 0 20px;
}

.dot-container {
  height: 6px;
  margin-bottom: 10px;
}

.route-line {
  top: 12px;
  position: relative;
}

.v-expansion-panels {
  box-shadow: none;
}

.upper-line {
  border-top: 1px solid rgba(0, 0, 0, 0.12) !important;
}
</style>

<style>
.pa-0 > .v-expansion-panel-header {
  padding: 0 !important;
}
</style>
